import React from "react";

/**
 * Renders a product description with proper spacing:
 * - Blank line -> paragraph break
 * - Line starting with "-", "*", "•", "→" -> bullet list item
 * - Line ending with ":" -> sub-heading (bold)
 * - Otherwise -> regular paragraph line (line-break preserved)
 *
 * Consecutive bullet lines are grouped into a single <ul>.
 */
const RichDescription = ({ text, className = "" }) => {
  if (!text) return null;

  const lines = String(text).split(/\r?\n/);
  const nodes = [];
  let bullets = [];

  const flushBullets = () => {
    if (bullets.length) {
      nodes.push(
        <ul
          key={`ul-${nodes.length}`}
          className="list-disc pl-5 space-y-1 mb-3 marker:text-primary"
        >
          {bullets.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>
      );
      bullets = [];
    }
  };

  lines.forEach((raw, idx) => {
    const line = raw.trim();

    // Blank line -> paragraph separator
    if (!line) {
      flushBullets();
      return;
    }

    // Bullet: "- ...", "* ...", "• ...", "→ ..."
    const bulletMatch = line.match(/^[-*•→]\s*(.+)$/);
    if (bulletMatch) {
      bullets.push(bulletMatch[1]);
      return;
    }

    // Sub-heading: line ending with ":"
    if (/:\s*$/.test(line) && line.length < 80) {
      flushBullets();
      nodes.push(
        <h4
          key={`h-${idx}`}
          className="font-semibold text-foreground mt-3 mb-1"
        >
          {line.replace(/:\s*$/, "")}
        </h4>
      );
      return;
    }

    // Normal paragraph line
    flushBullets();
    nodes.push(
      <p key={`p-${idx}`} className="mb-2 last:mb-0">
        {line}
      </p>
    );
  });

  flushBullets();

  return (
    <div
      className={`text-sm text-muted-foreground leading-relaxed ${className}`}
      data-testid="rich-description"
    >
      {nodes}
    </div>
  );
};

export default RichDescription;
